<template>
    <div class="resources-list">
        <v-text-field
            placeholder="Quick search"
            v-if="resources.length > minSearchAccounts"
            v-model="delayedFilter"
            prepend-icon="search"
            type="text"
            color="primary darken-4"
            autofocus
        ></v-text-field>
        <div v-else class="mb-6"></div>
        <v-list v-if="favourites.length">
          <resources-list-item v-for="(userResource, index) in favouriteItems"
                               :key="'fav'+index.toString()"
                               :resource="userResource"
                               :applicationID="applicationID"
                               :user="user"
                               :divider="index!==0"
                               :favourite="true"
                               :show-favourite="true"
                               @select="resourceSelect"
                               @favourite="onFavourite"
          ></resources-list-item>
        </v-list>
        <v-list>
            <resources-list-item v-for="(userResource, index) in nonFavouriteItems"
                                 :key="'resource' + index.toString()"
                                 :resource="userResource"
                                 :applicationID="applicationID"
                                 :user="user"
                                 :divider="index!==0"
                                 :favourite="favourites.indexOf(userResource.id) !== -1"
                                 :show-favourite="showFavourites && resources.length > minSearchAccounts"
                                 @select="resourceSelect"
                                 @favourite="onFavourite"
            ></resources-list-item>
        </v-list>
        <div class="d-flex justify-end">
            <v-chip outlined small v-if="resources.length > minSearchAccounts || resources.length !== filtered.length" color="primary darken-4" text-color="primary darken-4">
                Total {{resources.length}} accounts.
                <template v-if="filter.length">{{accountsFound}}</template>
            </v-chip>
        </div>

        <v-dialog v-model="error" persistent max-width="400">
            <v-card>
                <v-card-title class="headline">Login failed</v-card-title>
                <v-card-text>
                    Something goes wrong whilst trying to log you in.<br/>
                    Please check if you connected to internet.
                </v-card-text>
                <v-card-actions>
                    <v-btn text @click="logout">Logout</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="black" outlined @click="error=false">Try again</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import Vue from "vue";
    import ResourcesListItem from "./ResourcesListItem";

    export default {
        name: "ResourcesList",

        components: { ResourcesListItem },

        props: [
            'resources',
            'applicationID',
            'user'
        ],

        beforeRouteEnter(to, from, next) {
            Vue.prototype.$ticketGuard.check(to, from, next)
            Vue.prototype.$sessionGuard.check(to, from, next)
        },

        data() {
            return {
                minSearchAccounts: 10, // minimum number of results to show search bar
                filter: '',
                timeout: null,
                delayedFilter: '',
                error: false,
                counter: 0
            }
        },
        computed: {
            loading () {
                return this.$store.state.loading
            },

            filtered() {
                let filter = this.filter.trim().length ? this.filter.toLowerCase().split(' ') : [];
                const limit = filter.length ? 100 : 10;
                return this.resources
                    .filter(user => {
                        return !filter.length
                            || filter.every(term => {
                                return (user.resource.title || '').toLowerCase().indexOf(term) > -1
                                    || (user.resource.sub_title || '').toLowerCase().indexOf(term) > -1
                                    || (user.login.first_name || '').toLowerCase().indexOf(term) > -1
                                    || (user.login.last_name || '').toLowerCase().indexOf(term) > -1
                                    || (user.login.email || '').toLowerCase().indexOf(term) > -1
                                    || (user.login.phone || '').toLowerCase().indexOf(term) > -1
                                    || (user.roles || []).some(function (role) {
                                        return (role || '').toLowerCase().indexOf(term) > -1;
                                    });
                            });
                    })
                    .slice(0, limit);
            },

            accountsFound() {
                switch (this.filtered.length) {
                    case 0:
                        return 'No accounts found';
                    case 1:
                        return 'Found one account';
                    default:
                        return `Found ${this.filtered.length} accounts`;
                }
            },

            showFavourites() {
              return this.$sessionService.is_permanent();
            },

            favourites() {
              const favourites = this.$sessionService.get_favourites(this.applicationID);
              const slice = 0 * this.counter; // To trigger computed
              return favourites.slice(slice);
            },

            favouriteItems() {
              return this.filter.trim().length
                  ? []
                  : this.resources.filter(resource => this.favourites.indexOf(resource.id) !== -1);
            },

            nonFavouriteItems() {
              return this.filter.trim().length
                  ? this.filtered
                  : this.filtered.filter(resource => this.favourites.indexOf(resource.id) === -1)
            },
        },
        watch: {
            delayedFilter(val) {
                if (this.timeout) clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    this.filter = val;
                }, 600);
            },
        },
        methods: {
            async resourceSelect(userResource, adminMode) {
                adminMode = !!adminMode;

                this.$store.commit('loadingOn')
                this.$sessionService.store_last(this.applicationID)
                try {
                    window.location.href = await this.$ticketService.send(
                        this.$sessionService.get(),
                        userResource.id,
                        adminMode,
                        this.$pinSwitchService.get()
                    )
                } catch (e) {
                    this.error = true;
                }
            },

            onFavourite(userResource, isFavourite) {
              if (isFavourite) {
                this.$sessionService.add_favourites(this.applicationID, userResource.id);
              } else {
                this.$sessionService.remove_favourites(this.applicationID, userResource.id);
              }
              this.counter++;
            },

            logout() {
                this.$router.replace({ name: 'logout' });
            }
        },

        mounted() {
            if (this.filtered.length == 1) {
                let lastSelected = this.$sessionService.get_last();
                if (lastSelected !== this.applicationID) {
                    this.resourceSelect(this.filtered[0]);
                }
            }
        }
    }
</script>

<style scoped>
  .v-avatar {
      background-color: #e0e0e0;
  }
</style>
